/**
 * Exhaustion heatmap — TD Sequential counts across timeframes.
 *
 * Rows: timeframes (5 min → yearly). Columns: the last N bars, newest on the right.
 * Each cell is coloured by direction — green for a falling count (buy setup, a bottom
 * forming), red for a rising count (sell setup, a top forming) — with the intensity
 * rising toward 9, and the number inside is the count. A second, thinner row per
 * timeframe shows the countdown toward 13 in the same colouring.
 */

import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Chip, CircularProgress, Alert, Tooltip, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import apiService from '../../services/api';

const ALL_TF = ['2m', '5m', '15m', '60m', '360m', '1d', '1w', '1M', '1Q', '1Y'];

function cellStyle(v: number, max: number) {
  if (v === 0) return { bg: 'rgba(148,163,184,0.10)', fg: 'rgba(148,163,184,0.7)' };
  const a = Math.min(1, Math.abs(v) / max);
  const rgb = v < 0 ? '16,185,129' : '239,68,68';
  return { bg: `rgba(${rgb},${0.15 + 0.75 * a})`, fg: a > 0.55 ? '#fff' : `rgb(${rgb})` };
}

function Row({ r, bars }: { r: any; bars: number }) {
  const cells = r.cells || [];
  const pad = Math.max(0, bars - cells.length);
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
        <Box sx={{ width: 78, flexShrink: 0 }}>
          <Typography variant="caption" fontWeight="bold">{r.label}</Typography>
          {r.active && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.58rem', lineHeight: 1.1 }}>
              {r.active.direction} · cd {r.active.countdown}/13 {r.active.status !== 'running' ? `(${r.active.status})` : ''}
            </Typography>
          )}
        </Box>
        {r.error ? <Typography variant="caption" color="text.disabled">{r.error}</Typography> : (
          <Box sx={{ display: 'flex', gap: '2px', flex: 1, minWidth: 0 }}>
            {Array.from({ length: pad }).map((_, i) => <Box key={`p${i}`} sx={{ flex: 1, height: 26 }} />)}
            {cells.map((c: any, i: number) => {
              const st = cellStyle(c.setup, 9);
              const n = Math.abs(c.setup);
              return (
                <Tooltip key={i} title={`${c.t} · close ${c.close} · setup ${c.setup > 0 ? 'sell' : c.setup < 0 ? 'buy' : '—'} ${n || ''}${n === 9 ? ' ← 9!' : n > 9 ? ' (extended)' : ''}`}>
                  <Box sx={{ flex: 1, minWidth: 0, height: 26, borderRadius: '3px', bgcolor: st.bg, color: st.fg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.72rem', fontWeight: n >= 7 ? 800 : 600, outline: n === 9 ? '2px solid #F59E0B' : 'none' }}>
                    {n || ''}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
      {!r.error && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
          <Box sx={{ width: 78, flexShrink: 0 }}><Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.6rem' }}>countdown</Typography></Box>
          <Box sx={{ display: 'flex', gap: '2px', flex: 1, minWidth: 0 }}>
            {Array.from({ length: pad }).map((_, i) => <Box key={`q${i}`} sx={{ flex: 1, height: 14 }} />)}
            {cells.map((c: any, i: number) => {
              const st = cellStyle(c.countdown, 13);
              const n = Math.abs(c.countdown);
              return (
                <Tooltip key={i} title={`${c.t} · countdown ${n}/13`}>
                  <Box sx={{ flex: 1, minWidth: 0, height: 14, borderRadius: '2px', bgcolor: st.bg, color: st.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.58rem', fontWeight: 600,
                    outline: n === 13 ? '2px solid #A855F7' : 'none' }}>
                    {n || ''}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
}

export default function ExhaustionHeatmap({ symbol }: { symbol: string }) {
  const [bars, setBars] = useState(30);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!symbol) return;
    setLoading(true); setError(null);
    apiService.getTurningPointsHeatmap(symbol, ALL_TF.join(','), bars)
      .then(setData).catch(e => setError(e?.response?.data?.detail || e.message)).finally(() => setLoading(false));
  }, [symbol, bars]);

  return (
    <Card sx={{ overflow: 'hidden' }}><CardContent sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">Exhaustion heatmap — TD Sequential across timeframes</Typography>
        <Chip size="small" label="green = falling count (buy setup, bottom forming)" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(16,185,129,0.25)' }} />
        <Chip size="small" label="red = rising count (sell setup, top forming)" sx={{ height: 18, fontSize: '0.6rem', bgcolor: 'rgba(239,68,68,0.25)' }} />
        <Chip size="small" label="9 outlined = setup complete" variant="outlined" sx={{ height: 18, fontSize: '0.6rem', borderColor: '#F59E0B' }} />
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={14} />}
        <FormControl size="small" sx={{ minWidth: 110 }}>
          <InputLabel>Bars</InputLabel>
          <Select value={bars} label="Bars" onChange={e => setBars(Number(e.target.value))}>
            {[20, 30, 45, 60, 90].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Each row is one timeframe, newest bar on the right; the number is the setup count (1–9, extensions beyond 9 shown as the count), the thin row beneath is the countdown toward 13.
        Intraday rows come from stored bars (UTC); daily and above from the daily history. Hover a cell for its time and close.
      </Typography>
      {error && <Alert severity="warning">{error}</Alert>}
      {data && data.rows.map((r: any) => <Row key={r.timeframe} r={r} bars={bars} />)}
    </CardContent></Card>
  );
}
